import {
  ORDER_CANCELLED,
  ORDER_CONFIRMED,
  ORDER_PLACED,
  ORDER_RECEIVED,
} from "../../constants/orderStatus";
import {
  getTransitionKind,
  STATUS_SEQUENCE,
  TRANSITION_CANCEL,
  TRANSITION_FORWARD,
} from "./statusTransition";

// 注文docの日付フィールドの更新内容を組み立てる純関数群。
// firebaseに依存しない(Dateで受け渡しし、Timestampへの変換は functions/orders.js が行う)。
//
// 背景: 以前は「注文済」へ変更するたびに placed_at を無条件で現在時刻に上書きしていた。
// 検収済から発注済へ戻す手段が無く、取消→注文→発注と経由するしかなかったため、
// 経由中に注文日が書き換わり、期をまたぐと購入上限のカウントがずれていた(2026-08の不具合)。
// ここでは「既存の日付は原則そのまま。変えるときは操作者が明示する」を原則とする。

// ステータスと、そのステータスになった日を記録するフィールドの対応
export const STATUS_DATE_FIELD = {
  [ORDER_PLACED]: "placed_at",
  [ORDER_CONFIRMED]: "ordered_at",
  [ORDER_RECEIVED]: "received_at",
  [ORDER_CANCELLED]: "canceled_at",
};

// 画面表示・編集の並び(業務の進行順)
//
// autoRecorded: その日付を「操作した瞬間のシステム記録」として扱い、
//   手入力を一切受け付けないことを表す。ここが唯一の定義元で、
//   ステータス変更・日付編集・各ダイアログの表示はすべてこのフラグを見る。
export const ORDER_DATE_FIELDS = [
  { field: "placed_at", label: "注文日", status: ORDER_PLACED },
  { field: "ordered_at", label: "発注日", status: ORDER_CONFIRMED },
  {
    field: "received_at",
    label: "検収日",
    status: ORDER_RECEIVED,
    autoRecorded: true,
  },
  { field: "canceled_at", label: "取消日", status: ORDER_CANCELLED },
];

// 検収日を自動記録にしている理由(2026-08の問い合わせ):
// 給与控除は「発注処理画面で検収日を前月1日〜月末で検索 → 奉行用CSVを出力」で
// 月を締めている。締めた月の中へあとから検収日を書き込めると、その注文はどの月の
// 抽出にも入らず控除が永久に漏れる(担当者側では検知できない)。
// そのため検収日は編集可能な属性ではなく created_at と同じ「イベント時刻」として扱い、
// 検収済へ遷移した瞬間の時刻だけを記録する。
//
// 注文日は購入上限の"累計"カウントの基準であって月次の締めには使わないため、
// 従来どおり後から是正できる(それ自体が 2026-08 の不具合対応の目的だった)。
const AUTO_RECORDED_FIELD_SET = new Set(
  ORDER_DATE_FIELDS.filter(({ autoRecorded }) => autoRecorded).map(
    ({ field }) => field
  )
);

// その日付フィールドが自動記録(手入力不可)か
export const isAutoRecordedDateField = (field) =>
  AUTO_RECORDED_FIELD_SET.has(field);

// 変更履歴で使うフィールド名 → 表示名(statusも含む)
export const CHANGE_FIELD_LABEL = {
  status: "状態",
  ...ORDER_DATE_FIELDS.reduce(
    (acc, { field, label }) => ({ ...acc, [field]: label }),
    {}
  ),
};

// 注文docに追記する変更履歴の配列フィールド名。
// 新規作成時にはこのフィールドを作らない(arrayUnionで初回追記時に生える)ため、
// buildOrderData とそのテストには影響しない。
export const CHANGE_LOG_FIELD = "change_logs";
export const CHANGE_LOG_TYPE_STATUS = "status";
export const CHANGE_LOG_TYPE_DATE = "date";

// Firestore Timestamp / Date / 数値秒 を Date | null に正規化する。
// Timestampを直接importするとfirebase依存になるためダックタイピングで判定する。
export const toDateOrNull = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === "function") {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime())
      ? converted
      : null;
  }
  if (typeof value.seconds === "number") {
    return new Date(value.seconds * 1000);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// 2つの日付値(Timestamp/Date/null)が同じ時刻を指すか
export const isSameDateValue = (a, b) => {
  const dateA = toDateOrNull(a);
  const dateB = toDateOrNull(b);
  if (dateA === null || dateB === null) {
    return dateA === dateB;
  }
  return dateA.getTime() === dateB.getTime();
};

// カレンダーは日付しか選べないため、日付部分だけを差し替えて時刻は元の値を維持する。
// 元の値が無い場合は操作時刻(now)の時刻を使う。
// 例: 2026-06-29 10:12:44 に 2026/02/02 を指定 → 2026-02-02 10:12:44
export const mergeDateKeepingTime = (existing, nextDate, now) => {
  const next = toDateOrNull(nextDate);
  if (next === null) {
    return null;
  }
  const base = toDateOrNull(existing) || now || next;
  return new Date(
    next.getFullYear(),
    next.getMonth(),
    next.getDate(),
    base.getHours(),
    base.getMinutes(),
    base.getSeconds(),
    base.getMilliseconds()
  );
};

// ステータス変更ダイアログの日付欄の初期値を決める。
//   { enabled: チェック状態, date: カレンダーの初期日付 }
//
// 「その工程に新たに進む」ときだけ既定でONにし、今日を記録する。
// 工程を戻す・取消から復帰する場合は既定でOFF(=既存の日付を維持)にして、
// 元の注文日や発注日が黙って書き換わらないようにする。
// 一括で戻しが1件でも混ざる場合もOFFにする。OFFでも日付が未設定の注文には
// 今日が入るため、前進分の記録漏れは起きない。
export const isAdvancingTransition = (from, to) => {
  const kind = getTransitionKind(from, to);
  return kind === TRANSITION_FORWARD || kind === TRANSITION_CANCEL;
};

export const getDefaultDateInput = (orders, targetStatus, now) => {
  const list = orders || [];
  const field = STATUS_DATE_FIELD[targetStatus];
  // 自動記録の日付(検収日)はそもそも指定させない。
  // locked=true を見た画面はカレンダーではなく読み取り専用の注記を出す。
  // enabled は「操作者が日付を指定する」の意味なので false にしておく
  // (万一この値が buildStatusChangeUpdate に渡っても指定として扱われない)。
  if (isAutoRecordedDateField(field)) {
    return { enabled: false, date: now, locked: true };
  }
  const isAllAdvancing =
    list.length > 0 &&
    list.every((order) => isAdvancingTransition(order?.status, targetStatus));
  // 前進・取消は「今まさに起きた出来事」を記録するので、既存値が残っていても now を初期値にする。
  // 既存値を初期値にすると、取消→復帰→再取消のように前回の残骸が入っている場合に
  // 「前回の取消日」をそのまま確定してしまう。一括(list.length!==1)は元から now なので、
  // 単票と一括で結果が食い違う問題もここで解消する。
  // 後退・復帰のときだけ既存値を起点にする(ONにして確定しても値が変わらない)。
  const existing =
    !isAllAdvancing && list.length === 1 && field
      ? toDateOrNull(list[0]?.[field])
      : null;
  return { enabled: isAllAdvancing, date: existing || now };
};

// 工程を戻したときに「残ってしまう後工程の日付」を返す(値が入っているものだけ)。
//
// ステータス変更では後工程の日付を自動クリアしない(履歴を勝手に消さないため)。
// そのままだと日付フィルタやCSVに幽霊データとして残り続けるので、
// 確認ダイアログで「残ること」と「日付編集から消せること」を伝えるのに使う。
// 例: 検収済→発注済 に戻すと検収日が残る / 取消済→注文済 に戻すと取消日が残る。
export const getStaleDateFields = (order, targetStatus) => {
  if (targetStatus === ORDER_CANCELLED) {
    return [];
  }
  const targetIndex = STATUS_SEQUENCE.indexOf(targetStatus);
  if (targetIndex < 0) {
    return [];
  }
  return ORDER_DATE_FIELDS.filter(({ field, status }) => {
    if (toDateOrNull(order?.[field]) === null) {
      return false;
    }
    // 取消日は「取消済」以外の状態では不要
    if (status === ORDER_CANCELLED) {
      return true;
    }
    return STATUS_SEQUENCE.indexOf(status) > targetIndex;
  });
};

// ステータス変更時の更新内容を組み立てる。
//   dateInput: { enabled, date } — enabled=false なら
//     前進・取消 … 今回の出来事なので now を記録する
//     後退・復帰 … 既存値を維持する(未設定なら now)
//   自動記録の日付(検収日)では dateInput は無視され、常に now が記録される。
// 戻り値: { fields: 書き込むフィールド, changes: 変更履歴用の差分 }
export const buildStatusChangeUpdate = ({
  order,
  targetStatus,
  dateInput,
  now,
}) => {
  const field = STATUS_DATE_FIELD[targetStatus];
  const current = field ? toDateOrNull(order?.[field]) : null;
  // 前進・取消は「今起きた出来事」なので、前回の残骸が残っていても now で上書きする。
  // 後退・復帰は既存の日付を維持する(注文日を守るというこの機能の目的そのもの)。
  const isAdvancing = isAdvancingTransition(order?.status, targetStatus);
  // 自動記録の日付(検収日)は「その処理を実行した日」に限る。
  // 画面から何を渡されても採用せず、ステータスが実際に変わるなら必ず now を記録する。
  // 画面側でも入力欄を出していないが、規則はここ(ドメイン)に置いて
  // 呼び出し口が増えても遡り登録の穴が開かないようにする。
  const isAutoRecorded = isAutoRecordedDateField(field);
  const isStatusChanging = !!field && order?.status !== targetStatus;
  const explicitDate =
    !isAutoRecorded && dateInput?.enabled && toDateOrNull(dateInput?.date)
      ? dateInput.date
      : null;
  const nextValue =
    isAutoRecorded && isStatusChanging
      ? now
      : explicitDate
      ? // 前進・取消では時刻の起点も now にする(古い出来事の時刻を引き継がない)
        mergeDateKeepingTime(
          isAdvancing ? null : order?.[field],
          explicitDate,
          now
        )
      : isAdvancing
      ? now
      : current || now;

  const fields = { status: targetStatus };
  const changes = [];
  if (order?.status !== targetStatus) {
    changes.push({
      field: "status",
      from: order?.status ?? null,
      to: targetStatus,
    });
  }
  // 値が変わらないときはフィールド自体を書かない。
  // 画面を開いたままの古いコピーで確定しても、その間に他の管理者が
  // 「日付編集」で直した値を黙って巻き戻さないようにするため。
  if (field && !isSameDateValue(current, nextValue)) {
    fields[field] = nextValue;
    changes.push({ field, from: current, to: nextValue });
  }
  return { fields, changes };
};

// 日付のみの編集の更新内容を組み立てる。
//   input: { placed_at?: Date|null, ordered_at?: ..., canceled_at?: ... }
//   キーが無いフィールドは変更しない。null を渡すとクリアする。
//   自動記録の日付(検収日)は input に含まれていても無視する(設定もクリアもしない)。
export const buildDateEditUpdate = ({ order, input, now }) => {
  const fields = {};
  const changes = [];
  ORDER_DATE_FIELDS.forEach(({ field, autoRecorded }) => {
    // 検収日は給与控除の締めの基準なので、後から書き換える手段を一切用意しない
    if (autoRecorded) {
      return;
    }
    if (!input || !(field in input)) {
      return;
    }
    const current = toDateOrNull(order?.[field]);
    const requested = toDateOrNull(input[field]);
    const next = requested
      ? mergeDateKeepingTime(order?.[field], requested, now)
      : null;
    if (isSameDateValue(current, next)) {
      return;
    }
    fields[field] = next;
    changes.push({ field, from: current, to: next });
  });
  return { fields, changes };
};

// 変更履歴の1エントリを組み立てる。差分が無い場合はnull。
// 注: Firestoreは配列の中で serverTimestamp() を使えないため at はクライアント時刻。
//     update_user_email と同じ精度の情報として扱う。
export const buildChangeLogEntry = ({ type, changes, userEmail, now }) => {
  if (!changes || changes.length === 0) {
    return null;
  }
  return {
    at: now,
    by: userEmail || "",
    type,
    changes: changes.map(({ field, from, to }) => ({
      field,
      from: from ?? null,
      to: to ?? null,
    })),
  };
};

const isAfter = (a, b) => {
  const dateA = toDateOrNull(a);
  const dateB = toDateOrNull(b);
  return dateA !== null && dateB !== null && dateA.getTime() > dateB.getTime();
};

// 日付編集の妥当性チェック。
//   errors   … 保存を止める(注文日の欠落のみ)
//   warnings … 注意喚起のみで保存はできる
//
// 順序の逆転やステータスとの矛盾をエラーにしないのは、この機能自体が
// 「イレギュラーになってしまったデータを是正する」ためのものだから。
// ブロックすると本来の目的を果たせなくなる。
export const validateOrderDates = ({ values, status, now }) => {
  const errors = [];
  const warnings = [];
  const placedAt = toDateOrNull(values?.placed_at);
  const orderedAt = toDateOrNull(values?.ordered_at);
  const receivedAt = toDateOrNull(values?.received_at);
  const canceledAt = toDateOrNull(values?.canceled_at);

  // 注文日が無いと購入上限の集計(placed_at >= 期首)から黙って外れてしまう
  if (!placedAt) {
    errors.push(
      "注文日は必須です。空にすると購入上限の集計対象から外れてしまいます。"
    );
  }

  // 順序(注文 → 発注 → 検収)。未設定はスキップする
  if (isAfter(placedAt, orderedAt)) {
    warnings.push("発注日が注文日より前になっています。");
  }
  if (isAfter(orderedAt, receivedAt)) {
    warnings.push("検収日が発注日より前になっています。");
  }
  if (!orderedAt && isAfter(placedAt, receivedAt)) {
    warnings.push("検収日が注文日より前になっています。");
  }

  // 未来日
  if (now) {
    ORDER_DATE_FIELDS.forEach(({ field, label }) => {
      if (isAfter(values?.[field], now)) {
        warnings.push(`${label}に未来の日付が設定されています。`);
      }
    });
  }

  // ステータスとの矛盾
  if (status === ORDER_CONFIRMED && !orderedAt) {
    warnings.push("状態が「発注済」ですが発注日が未設定です。");
  }
  if (status === ORDER_RECEIVED && !receivedAt) {
    warnings.push("状態が「検収済」ですが検収日が未設定です。");
  }
  if (status === ORDER_CANCELLED && !canceledAt) {
    warnings.push("状態が「取消済」ですが取消日が未設定です。");
  }
  if (status && status !== ORDER_CANCELLED && canceledAt) {
    warnings.push(`状態が「${status}」ですが取消日が入っています。`);
  }
  if (status === ORDER_PLACED && orderedAt) {
    warnings.push("状態が「注文済」ですが発注日が入っています。");
  }
  // 「検収済でないのに検収日が入っている」は警告しない。
  // 検収日は自動記録でクリアできないため、工程を戻した注文では必ずこの状態になる。
  // 直す手段が無いものを警告すると、同じダイアログの中で
  // 「直せ」と「直せない」が並ぶことになる。
  // 代わりに日付編集ダイアログのロック行が事実として説明する
  // (functions/order/orderDateMessages.js の buildAutoRecordedEditNote)。

  return { errors, warnings };
};
